import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Supported currencies
export type Currency = 'USD' | 'SAR' | 'EGP';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  currencySymbol: string;
  getCurrencySymbol: (currency: Currency | string) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const CURRENCY_STORAGE_KEY = 'gaith_selected_currency';

const getCurrencySymbolFn = (currency: Currency | string): string => {
  switch (currency?.toUpperCase()) {
    case 'USD':
      return '$';
    case 'SAR':
      return 'SAR';
    case 'EGP':
      return 'EGP';
    default:
      return currency || '$';
  }
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  // Initialize from localStorage or default to USD
  const [currency, setCurrencyState] = useState<Currency>(() => {
    const saved = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (saved && ['USD', 'SAR', 'EGP'].includes(saved)) {
      return saved as Currency;
    }
    return 'USD';
  });

  // Persist to localStorage whenever currency changes
  useEffect(() => {
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
  }, [currency]);

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
  };

  const value: CurrencyContextType = {
    currency,
    setCurrency,
    currencySymbol: getCurrencySymbolFn(currency),
    getCurrencySymbol: getCurrencySymbolFn
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
